import React, { useState } from 'react';
import { Modal, Button, Form, Input, Select, DatePicker, InputNumber, message, Steps, Card, Typography, Space, Divider } from 'antd';
import { 
    MailOutlined, 
    UserOutlined, 
    CalendarOutlined, 
    TeamOutlined, 
    PhoneOutlined,
    EnvironmentOutlined,
    MessageOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';
import api from '../../services/api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const OrderAssistanceModal = ({ visible, onClose, menuItem }) => {
    const [form] = Form.useForm();
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [orderDetails, setOrderDetails] = useState({});

    const steps = [
        {
            title: 'Event Details',
            icon: <CalendarOutlined />,
        },
        {
            title: 'Requirements',
            icon: <MessageOutlined />,
        },
        {
            title: 'Contact Info',
            icon: <UserOutlined />,
        },
        {
            title: 'Review',
            icon: <CheckCircleOutlined />,
        },
    ];

    const handleNext = () => {
        form.validateFields().then(() => {
            const values = form.getFieldsValue();
            setOrderDetails(prev => ({ ...prev, ...values }));
            setCurrentStep(currentStep + 1);
        }).catch(() => {
            message.error('Please fill in all required fields');
        });
    };

    const handlePrev = () => {
        setCurrentStep(currentStep - 1);
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            
            // Create lead with order details
            const leadData = {
                name: orderDetails.name,
                email: orderDetails.email,
                phone: orderDetails.phone,
                event_type: orderDetails.eventType,
                event_date: orderDetails.eventDate,
                guest_count: orderDetails.guestCount,
                location: orderDetails.location,
                budget: orderDetails.budget,
                special_requirements: orderDetails.requirements,
                dietary_restrictions: orderDetails.dietaryRestrictions,
                menu_item_id: menuItem?.id,
                menu_item_name: menuItem?.name,
                status: 'new',
                source: 'ai_assistance'
            };

            // Submit lead
            const leadResponse = await api.createLead(leadData);
            
            if (leadResponse.success) {
                // Send email with order details
                const emailData = {
                    to: 'connect.spoonandspice@gmail.com',
                    subject: `New Order Request - ${orderDetails.eventType} - ${orderDetails.name}`,
                    template: 'order_request',
                    data: {
                        lead: leadData,
                        menuItem: menuItem,
                        timestamp: new Date().toISOString()
                    }
                };

                await api.sendEmail(emailData);
                
                message.success('Your order request has been submitted successfully! We will contact you soon.');
                onClose();
                form.resetFields();
                setCurrentStep(0);
                setOrderDetails({});
            } else {
                message.error('Failed to submit order request. Please try again.');
            }
        } catch (error) {
            console.error('Order submission error:', error);
            message.error('Failed to submit order request. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 0:
                return (
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <div>
                            <Title level={4}>Event Details</Title>
                            <Text type="secondary">Tell us about your event</Text>
                        </div>
                        
                        <Form.Item
                            name="eventType"
                            label="Event Type"
                            rules={[{ required: true, message: 'Please select event type' }]}
                        >
                            <Select placeholder="Select event type">
                                <Option value="wedding">Wedding</Option>
                                <Option value="birthday">Birthday Party</Option>
                                <Option value="corporate">Corporate Event</Option>
                                <Option value="anniversary">Anniversary</Option>
                                <Option value="festival">Festival Celebration</Option>
                                <Option value="other">Other</Option>
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="eventDate"
                            label="Event Date"
                            rules={[{ required: true, message: 'Please select event date' }]}
                        >
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>

                        <Form.Item
                            name="guestCount"
                            label="Number of Guests"
                            rules={[{ required: true, message: 'Please enter guest count' }]}
                        >
                            <InputNumber
                                min={1}
                                max={1000}
                                style={{ width: '100%' }}
                                placeholder="Enter number of guests"
                            />
                        </Form.Item>

                        <Form.Item
                            name="location"
                            label="Event Location"
                            rules={[{ required: true, message: 'Please enter event location' }]}
                        >
                            <Input
                                prefix={<EnvironmentOutlined />}
                                placeholder="Enter event location"
                            />
                        </Form.Item>
                    </Space>
                );

            case 1:
                return (
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <div>
                            <Title level={4}>Special Requirements</Title>
                            <Text type="secondary">Any specific requirements or preferences?</Text>
                        </div>

                        <Form.Item
                            name="requirements"
                            label="Special Requirements"
                        >
                            <TextArea
                                rows={4}
                                placeholder="e.g., specific dishes, presentation style, serving method, etc."
                            />
                        </Form.Item>

                        <Form.Item
                            name="dietaryRestrictions"
                            label="Dietary Restrictions"
                        >
                            <Select
                                mode="multiple"
                                placeholder="Select dietary restrictions"
                                style={{ width: '100%' }}
                            >
                                <Option value="vegetarian">Vegetarian</Option>
                                <Option value="vegan">Vegan</Option>
                                <Option value="gluten_free">Gluten-Free</Option>
                                <Option value="dairy_free">Dairy-Free</Option>
                                <Option value="nut_allergy">Nut Allergy</Option>
                                <Option value="halal">Halal</Option>
                                <Option value="jain">Jain</Option>
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="budget"
                            label="Budget Range (₹)"
                        >
                            <Select placeholder="Select budget range">
                                <Option value="under_50000">Under ₹50,000</Option>
                                <Option value="50000_100000">₹50,000 - ₹1,00,000</Option>
                                <Option value="100000_200000">₹1,00,000 - ₹2,00,000</Option>
                                <Option value="200000_500000">₹2,00,000 - ₹5,00,000</Option>
                                <Option value="above_500000">Above ₹5,00,000</Option>
                            </Select>
                        </Form.Item>
                    </Space>
                );

            case 2:
                return (
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <div>
                            <Title level={4}>Contact Information</Title>
                            <Text type="secondary">How can we reach you?</Text>
                        </div>

                        <Form.Item
                            name="name"
                            label="Full Name"
                            rules={[{ required: true, message: 'Please enter your name' }]}
                        >
                            <Input
                                prefix={<UserOutlined />}
                                placeholder="Enter your full name"
                            />
                        </Form.Item>

                        <Form.Item
                            name="email"
                            label="Email Address"
                            rules={[
                                { required: true, message: 'Please enter your email' },
                                { type: 'email', message: 'Please enter a valid email' }
                            ]}
                        >
                            <Input
                                prefix={<MailOutlined />}
                                placeholder="Enter your email address"
                            />
                        </Form.Item>

                        <Form.Item
                            name="phone"
                            label="Phone Number"
                            rules={[{ required: true, message: 'Please enter your phone number' }]}
                        >
                            <Input
                                prefix={<PhoneOutlined />}
                                placeholder="Enter your phone number"
                            />
                        </Form.Item>
                    </Space>
                );

            case 3:
                return (
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <div>
                            <Title level={4}>Review Your Order Request</Title>
                            <Text type="secondary">Please review your details before submitting</Text>
                        </div>

                        <Card title="Event Details" size="small">
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <div><Text strong>Event Type:</Text> {orderDetails.eventType}</div>
                                <div><Text strong>Date:</Text> {orderDetails.eventDate?.format('DD/MM/YYYY')}</div>
                                <div><Text strong>Guests:</Text> {orderDetails.guestCount}</div>
                                <div><Text strong>Location:</Text> {orderDetails.location}</div>
                            </Space>
                        </Card>

                        <Card title="Contact Information" size="small">
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <div><Text strong>Name:</Text> {orderDetails.name}</div>
                                <div><Text strong>Email:</Text> {orderDetails.email}</div>
                                <div><Text strong>Phone:</Text> {orderDetails.phone}</div>
                            </Space>
                        </Card>

                        {orderDetails.requirements && (
                            <Card title="Special Requirements" size="small">
                                <Text>{orderDetails.requirements}</Text>
                            </Card>
                        )}

                        {menuItem && (
                            <Card title="Menu Item of Interest" size="small">
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <div><Text strong>Dish:</Text> {menuItem.name}</div>
                                    <div><Text strong>Price:</Text> ₹{menuItem.price}</div>
                                    {menuItem.description && (
                                        <div><Text strong>Description:</Text> {menuItem.description}</div>
                                    )}
                                </Space>
                            </Card>
                        )}
                    </Space>
                );

            default:
                return null;
        }
    };

    return (
        <Modal
            title={
                <Space>
                    <MailOutlined style={{ color: '#1890ff' }} />
                    <span>AI Order Assistance</span>
                </Space>
            }
            open={visible}
            onCancel={onClose}
            width={600}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    Cancel
                </Button>,
                currentStep > 0 && (
                    <Button key="prev" onClick={handlePrev}>
                        Previous
                    </Button>
                ),
                currentStep < steps.length - 1 ? (
                    <Button key="next" type="primary" onClick={handleNext}>
                        Next
                    </Button>
                ) : (
                    <Button
                        key="submit"
                        type="primary"
                        loading={loading}
                        onClick={handleSubmit}
                    >
                        Submit Order Request
                    </Button>
                ),
            ]}
            destroyOnClose
        >
            <Form form={form} layout="vertical">
                <Steps current={currentStep} items={steps} style={{ marginBottom: 24 }} />
                
                {renderStepContent()}
            </Form>
        </Modal>
    );
};

export default OrderAssistanceModal;
